import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Upload, FileCheck, Crown, Trash2, Eye, Loader2, Building2 } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { agencyMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill, VerifiedBadge, LoadingScreen, inr } from "@/components/common";

const DOC_TYPES = ["PSARA Licence", "GST Certificate", "PAN", "Company Registration", "Address Proof", "Owner/Director KYC", "Insurance", "Labour Registration", "Training Records"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AgencyProfileEdit() {
  const [a, setA] = useState(null);
  const [plans, setPlans] = useState([]);
  const [docs, setDocs] = useState([]);
  const [newDoc, setNewDoc] = useState({ doc_type: "", expiry_date: "" });
  const [docFile, setDocFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoRef = useRef();
  const docRef = useRef();

  const load = async () => {
    const { data } = await api.get("/agencies/mine/profile"); setA(data);
    api.get("/agencies/documents/mine").then((r) => setDocs(r.data)).catch(() => {});
  };
  useEffect(() => { load().catch(() => {}); api.get("/packages").then((r) => setPlans(r.data)).catch(() => {}); }, []);
  if (!a) return <DashboardShell menu={agencyMenu} title="Agency Profile"><LoadingScreen /></DashboardShell>;

  const save = async () => {
    try {
      await api.put("/agencies/mine/profile", {
        ...a, service_locations: Array.isArray(a.service_locations) ? a.service_locations : String(a.service_locations || "").split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Profile updated");
    } catch (e) { toast.error("Failed"); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "logo");
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setA({ ...a, logo: data.url });
      await api.put("/agencies/mine/profile", { ...a, logo: data.url });
      toast.success("Logo updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setLogoUploading(false); }
  };

  const upload = async () => {
    if (!newDoc.doc_type) return toast.error("Select document type");
    if (!docFile) return toast.error("Choose a file to upload");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", docFile); fd.append("kind", "document");
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/agencies/documents", { ...newDoc, file_url: data.url, file_path: data.path, file_id: data.id, original_filename: data.original_filename });
      toast.success("Document submitted for verification (status: PENDING)");
      setNewDoc({ doc_type: "", expiry_date: "" }); setDocFile(null); if (docRef.current) docRef.current.value = ""; load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setUploading(false); }
  };

  const delDoc = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/agencies/documents/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  const setPlan = async (plan) => {
    try { await api.post("/subscription/set", { plan }); toast.success(`Subscription set to ${plan}`); load(); } catch (e) { toast.error("Failed"); }
  };

  return (
    <DashboardShell menu={agencyMenu} title="Agency Profile">
      <div className="flex items-center gap-3 mb-6"><VerifiedBadge verified={a.verified} /><StatusPill status={a.verification_status} /><span className="text-sm text-navy-500">Plan: <b>{a.subscription_plan}</b></span></div>
      <Tabs defaultValue="profile">
        <TabsList className="mb-5"><TabsTrigger value="profile" data-testid="tab-profile">Company Profile</TabsTrigger><TabsTrigger value="docs" data-testid="tab-docs">Documents</TabsTrigger><TabsTrigger value="plan" data-testid="tab-plan">Subscription</TabsTrigger></TabsList>

        <TabsContent value="profile">
          <div className="bg-white border border-navy-200 rounded-xl p-6 space-y-4 max-w-3xl">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-xl border border-navy-200 bg-navy-50 flex items-center justify-center overflow-hidden shrink-0">
                {a.logo ? <img src={`${BACKEND}${a.logo}`} alt="logo" className="w-full h-full object-contain" /> : <Building2 className="text-navy-300" />}
              </div>
              <div>
                <Label className="text-xs">Company Logo</Label>
                <input ref={logoRef} type="file" accept="image/*" hidden onChange={uploadLogo} data-testid="p-logo-input" />
                <div><Button variant="outline" className="border-navy-300 mt-1" disabled={logoUploading} onClick={() => logoRef.current?.click()} data-testid="p-logo-btn">
                  {logoUploading ? <Loader2 size={15} className="animate-spin mr-1" /> : <Upload size={15} className="mr-1" />} {a.logo ? "Replace logo" : "Upload logo"}</Button></div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs">Agency Name</Label><Input value={a.name} onChange={(e) => setA({ ...a, name: e.target.value })} data-testid="p-name" /></div>
              <div><Label className="text-xs">Owner / Director</Label><Input value={a.owner_name || ""} onChange={(e) => setA({ ...a, owner_name: e.target.value })} data-testid="p-owner" /></div>
              <div><Label className="text-xs">Contact Mobile</Label><Input value={a.mobile || ""} onChange={(e) => setA({ ...a, mobile: e.target.value })} data-testid="p-mobile" /></div>
              <div><Label className="text-xs">Business Type</Label><Input value={a.business_type || ""} onChange={(e) => setA({ ...a, business_type: e.target.value })} data-testid="p-biztype" /></div>
              <div><Label className="text-xs">Price/guard/month (₹)</Label><Input type="number" value={a.price_per_guard_month} onChange={(e) => setA({ ...a, price_per_guard_month: Number(e.target.value) })} data-testid="p-price" /></div>
              <div><Label className="text-xs">PSARA Number</Label><Input value={a.psara_number || ""} onChange={(e) => setA({ ...a, psara_number: e.target.value })} data-testid="p-psara" /></div>
              <div><Label className="text-xs">GST Number</Label><Input value={a.gst_number || ""} onChange={(e) => setA({ ...a, gst_number: e.target.value })} data-testid="p-gst" /></div>
              <div><Label className="text-xs">PAN</Label><Input value={a.pan || ""} onChange={(e) => setA({ ...a, pan: e.target.value })} data-testid="p-pan" /></div>
              <div><Label className="text-xs">Years Experience</Label><Input type="number" value={a.years_experience} onChange={(e) => setA({ ...a, years_experience: Number(e.target.value) })} data-testid="p-exp" /></div>
              <div><Label className="text-xs">Available Guards</Label><Input type="number" value={a.available_guards || 0} onChange={(e) => setA({ ...a, available_guards: Number(e.target.value) })} data-testid="p-guards" /></div>
              <div className="sm:col-span-2"><Label className="text-xs">Service Locations (comma separated)</Label><Input value={Array.isArray(a.service_locations) ? a.service_locations.join(", ") : a.service_locations} onChange={(e) => setA({ ...a, service_locations: e.target.value.split(",").map((s) => s.trim()) })} data-testid="p-locations" /></div>
            </div>
            <div><Label className="text-xs">Description</Label><Textarea rows={3} value={a.description || ""} onChange={(e) => setA({ ...a, description: e.target.value })} data-testid="p-desc" /></div>
            <Button className="bg-brand hover:bg-brand-600" onClick={save} data-testid="save-profile-btn">Save Changes</Button>
          </div>
        </TabsContent>

        <TabsContent value="docs">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800 mb-4">
            Uploading a document does not make your agency verified. Our platform team reviews each document before granting the Verified badge.
          </div>
          <div className="bg-white border border-navy-200 rounded-xl p-6 max-w-3xl">
            <div className="flex flex-wrap items-end gap-3 mb-5">
              <div className="flex-1 min-w-[160px]"><Label className="text-xs">Document Type</Label>
                <Select value={newDoc.doc_type} onValueChange={(v) => setNewDoc({ ...newDoc, doc_type: v })}><SelectTrigger data-testid="doc-type"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs">Expiry Date</Label><Input type="date" value={newDoc.expiry_date} onChange={(e) => setNewDoc({ ...newDoc, expiry_date: e.target.value })} data-testid="doc-expiry" /></div>
              <div><Label className="text-xs">File (PDF/Image)</Label><Input ref={docRef} type="file" accept="image/*,application/pdf" onChange={(e) => setDocFile(e.target.files?.[0] || null)} data-testid="doc-file" /></div>
              <Button className="bg-navy-900 hover:bg-navy-800" disabled={uploading} onClick={upload} data-testid="upload-doc-btn">{uploading ? <Loader2 size={16} className="animate-spin mr-1" /> : <Upload size={16} className="mr-1" />} Submit</Button>
            </div>
            {docs.length === 0 ? <p className="text-navy-500 text-sm">No documents submitted yet.</p> : (
              <table className="w-full text-sm">
                <thead className="bg-navy-50 text-navy-500 text-xs uppercase"><tr><th className="text-left p-3">Document</th><th className="p-3">Expiry</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
                <tbody>{docs.map((d) => (<tr key={d.id} className="border-t border-navy-100" data-testid={`doc-row-${d.id}`}>
                  <td className="p-3 flex items-center gap-2 text-navy-800"><FileCheck size={16} className="text-brand" /> {d.doc_type}<span className="text-xs text-navy-400">{d.original_filename}</span></td>
                  <td className="p-3 text-center text-navy-600">{d.expiry_date || "—"}</td>
                  <td className="p-3 text-center"><StatusPill status={d.status} /></td>
                  <td className="p-3 text-center">
                    <div className="flex gap-1 justify-center">
                      {(d.file_id || d.file_url) && <a href={d.file_id ? `${BACKEND}/api/files/doc/${d.file_id}` : `${BACKEND}${d.file_url}`} target="_blank" rel="noreferrer" title="Preview"><Button size="icon" variant="outline" className="h-8 w-8 border-navy-300" data-testid={`doc-view-${d.id}`}><Eye size={14} /></Button></a>}
                      <Button size="icon" variant="outline" className="h-8 w-8 border-red-200 text-red-600 hover:bg-red-50" onClick={() => delDoc(d.id)} data-testid={`doc-delete-${d.id}`}><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>))}</tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="plan">
          <div className="grid md:grid-cols-3 gap-5 max-w-4xl">
            {plans.map((p) => {
              const name = p.name;
              const isCurrent = a.subscription_plan === name;
              return (
                <div key={p.code} className={`rounded-xl border p-6 ${isCurrent ? "border-brand ring-2 ring-brand" : "border-navy-200"} bg-white`}>
                  <div className="flex items-center gap-2">{p.highlight && <Crown size={18} className="text-amber-500" />}<h3 className="font-heading font-bold text-lg text-navy-900">{name}</h3></div>
                  <div className="mt-2 font-heading text-2xl font-extrabold">{p.price === 0 ? "₹0" : inr(p.price)}<span className="text-sm text-navy-500 font-normal">/{(p.billing_period || "month").replace("ly", "")}</span></div>
                  <Button className={`mt-4 w-full ${isCurrent ? "bg-navy-200 text-navy-500" : "bg-brand hover:bg-brand-600"}`} disabled={isCurrent} onClick={() => setPlan(p.code)} data-testid={`set-plan-${name}`}>
                    {isCurrent ? "Current Plan" : "Choose"}
                  </Button>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </DashboardShell>
  );
}
